import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import AuthCard from "@/components/auth/AuthCard";
import AuthButton from "@/components/auth/AuthButton";
import { useAuth, formatAuthError } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRICE_BASIC = 19.99;
const fmt = (n) => `US$${n.toFixed(2)}`;
const PLAN_PRICE_LABEL = process.env.REACT_APP_PLAN_PRICE || "NZ$35.00";

// PurchasePlan — CDI Master Rule: Trial + Basic must NEVER see Premium.
// Basic is the only purchasable tier surfaced on this page. Premium
// buttons, rows, "Coming Soon" labels, teasers — all removed.
export default function PurchasePlan() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [basicTicked, setBasicTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const email = user?.email || "";
  const hasBasic = !!user?.basic_owned;

  const buyBasic = async () => {
    if (!email) { toast.error("Sign in first."); return; }
    setBusy(true);
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const r = await axios.post(`${API}/stripe/basic-checkout`, { email, origin_url: origin });
      const url = r.data?.checkout_url;
      if (!url) throw new Error("No checkout URL returned.");
      window.location.href = url;
    } catch (err) {
      toast.error(formatAuthError(err));
      setBusy(false);
    }
  };

  if (hasBasic) {
    return (
      <AuthCard
        title="Premium coming soon"
        subtitle=""
      >
        <AuthButton variant="charcoal" onClick={() => navigate("/")} testid="purchase-plan-cancel">Back</AuthButton>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Unlock CDI Collectables"
      subtitle="Basic unlocks the full app."
    >
      <div
        data-testid="purchase-plan-basic-row"
        className="mb-4 p-4 rounded-md cursor-pointer transition-colors"
        style={{
          background: basicTicked ? "#231d10" : "#0f0f0f",
          border: `1px solid ${basicTicked ? "#C9A86A" : "#2a2a2a"}`,
        }}
        onClick={() => setBasicTicked((v) => !v)}
      >
        <div className="flex items-start gap-3">
          <div
            aria-hidden
            data-testid="purchase-plan-basic-check"
            style={{
              width: 18, height: 18, borderRadius: 4,
              border: `2px solid ${basicTicked ? "#C9A86A" : "#555"}`,
              background: basicTicked ? "#C9A86A" : "transparent",
              flexShrink: 0, marginTop: 2,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#000", fontSize: 12, fontWeight: 900,
            }}
          >{basicTicked ? "✓" : ""}</div>
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold" style={{ color: "#f7f7f7" }}>
                CDI Basic Access
              </div>
              <div className="text-xs font-semibold" style={{ color: "#C9A86A" }}>{PLAN_PRICE_LABEL}</div>
            </div>
            <div className="text-xs mt-1" style={{ color: "#9a9a9a" }}>
              One-time purchase — unlocks the full app.
            </div>
          </div>
        </div>
      </div>

      <div
        data-testid="purchase-plan-summary"
        className="mt-5 mb-5 p-4 rounded-md text-xs"
        style={{ background: "#0f0f0f", border: "1px solid #2a2a2a", color: "#c9c9c9" }}
      >
        <div className="font-semibold mb-2" style={{ color: "#f7f7f7" }}>Payment summary</div>
        {basicTicked ? (
          <>
            <div className="flex justify-between py-1" data-testid="purchase-plan-summary-basic">
              <span>Basic — one-time</span>
              <span style={{ color: "#f7f7f7" }}>{PLAN_PRICE_LABEL}</span>
            </div>
            <div
              data-testid="purchase-plan-summary-total"
              className="flex justify-between py-2 mt-2 font-semibold"
              style={{ borderTop: "1px solid #2a2a2a", color: "#C9A86A" }}
            >
              <span>Total due today</span>
              <span>{PLAN_PRICE_LABEL}</span>
            </div>
          </>
        ) : (
          <div style={{ color: "#6f6f6f" }} data-testid="purchase-plan-summary-empty">
            Tick Basic to continue.
          </div>
        )}
      </div>

      {basicTicked && (
        <AuthButton
          onClick={buyBasic}
          loading={busy}
          disabled={busy}
          testid="purchase-plan-continue"
        >Continue to Payment →</AuthButton>
      )}
      <div className={basicTicked ? "mt-3" : ""}>
        <AuthButton variant="charcoal" onClick={() => navigate("/")} testid="purchase-plan-cancel">Cancel</AuthButton>
      </div>
    </AuthCard>
  );
}
